import React, { useMemo, useState } from 'react';
import { Button, Col, Container, Form, Row, Spinner, Table } from 'react-bootstrap';
import { ENDPOINTS, expenseFields, incomeFields, pnlOptions, staticFields } from '../helpers/constants';
import { calculateRebate, calculateTotalGP, getPnlValue } from '../helpers/global';
import SlidingSideBar from './SlidingSideBar/SlidingSideBar';
import { toast } from 'react-toastify';
import { makeApiRequests } from '../helpers/api';

const orderedLabels = [
  'Buyer',
  'Salesperson',
  'Status',
  'Funding Status',
  'Size Overall',
  'Invoice',
  'Del & set',
  'Air Condition',
  'Skirting',
  'Utilities',
  'Commission Due',
  'Team Leader',
  'Admin',
  'Service',
  'Bank fees',
  'SP/Comm',
  'Rebate',
  'Frg. Reim',
  'NPS',
  'Doc Prep',
  'TOTAL GP',
  'Bank',
  'Factory',
  'Date of Deposit'
].map(label => label.toLowerCase());

const PnLSidebar = ({ contract, onClose, onUpdate, appChoices, visible }) => {
  const [inputValues, setInputValues] = useState(() => {
    return pnlOptions.map(option => ({
      ...option,
      value: option.key === 'pnlRebate' ? calculateRebate(contract) : getPnlValue(contract, option.key, appChoices)
    }));
  });

  const [updatingPnl, setUpdatingPnl] = useState(false);

  const contractStatus = useMemo(() => (contract?.newStatus === 'Contracted' ? 'Contracted' : 'Working'), [contract]);

  const totalGP = calculateTotalGP(
    {
      ...contract,
      pnlOptions: inputValues,
      pnlRebate: inputValues?.find(option => option?.key === 'pnlRebate')?.value || 0
    },
    appChoices,
    expenseFields,
    incomeFields(contractStatus)
  );

  const handleInputChange = (key, value) => {
    setInputValues(prev =>
      prev.map(option => (option.key === key ? { ...option, value: value ? Number(value) : null } : option))
    );
  };

  const handleUpdate = async () => {
    setUpdatingPnl(true);
    toast.info('Updating P&L details...');

    const { response, error } = await makeApiRequests({
      endpoint:
        contract?.newStatus === 'Contracted'
          ? ENDPOINTS.CONTRACTS_WITH_ID(contract._id)
          : ENDPOINTS.CONTACTS_WITH_ID(contract._id),
      method: 'PUT',
      requestBody: {
        pnlOptions: inputValues?.slice(0, -1),
        pnlRebate: inputValues?.find(option => option?.key === 'pnlRebate')?.value
      }
    });

    setUpdatingPnl(false);

    if (error) {
      toast.error(error);
      return;
    }

    toast.success('P&L details updated successfully!');
    onUpdate(response);
  };

  const allFields = useMemo(() => {
    return [
      ...inputValues.map(f => ({ ...f, type: 'input' })),
      ...staticFields(contractStatus).map(f => ({ ...f, type: 'static' })),
      {
        label: 'Total GP',
        value: () => totalGP?.toFixed(2) || '0',
        type: 'static'
      }
    ].sort((a, b) => orderedLabels.indexOf(a.label.toLowerCase()) - orderedLabels.indexOf(b.label.toLowerCase()));
  }, [inputValues, contractStatus, totalGP]);

  return (
    <SlidingSideBar visible={visible} onClose={onClose} title="P&L Details">
      <Container fluid>
        <Table bordered responsive>
          <thead>
            <tr className="bg-primary text-white">
              <th className="py-2">Category</th>
              <th className="py-2">Amount</th>
            </tr>
          </thead>
          <tbody>
            {allFields.map(field => (
              <tr key={field.label} className={field.type === 'static' ? 'bg-light' : ''}>
                <td>{field.label}</td>
                <td>
                  {field.type === 'input' ? (
                    <Form.Control
                      type="number"
                      placeholder="Enter amount"
                      size="sm"
                      value={field.value !== null ? field.value : ''}
                      onChange={e => handleInputChange(field.key, e.target.value)}
                    />
                  ) : (
                    <span className="font-weight-bold">{field.value(contract)}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Container>
      <div
        style={{
          position: 'sticky',
          bottom: 0,
          backgroundColor: 'white',
          borderTop: '1px solid #dee2e6',
          padding: '1rem',
          zIndex: 2
        }}
      >
        <Row>
          <Col className="d-flex justify-content-end">
            <Button variant="secondary" className="mr-2" onClick={onClose} disabled={updatingPnl}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleUpdate} disabled={updatingPnl}>
              {updatingPnl ? <Spinner animation="border" size="sm" className="mr-1" /> : null}
              {updatingPnl ? 'Updating...' : 'Update'}
            </Button>
          </Col>
        </Row>
      </div>
    </SlidingSideBar>
  );
};

export default PnLSidebar;
